/**
 * Episode of Care member add form — Zod contract for EpisodeOfCarePage.
 * Relationship codes align with v3-RoleCode subset in fhir-model and manage-eoc-party bot.
 */
import { z } from 'zod';
import type { UnifyRelationshipCode } from '@unify/fhir-model/resources/related-person';
import { relationshipCodeValues } from './related-person.schema';

export type EpisodePartyRelationshipCode = UnifyRelationshipCode;

export const EPISODE_PARTY_RELATIONSHIP_OPTIONS: ReadonlyArray<{
  value: EpisodePartyRelationshipCode;
  label: string;
}> = [
  { value: 'PRN', label: 'Parent' },
  { value: 'FTH', label: 'Father' },
  { value: 'MTH', label: 'Mother' },
  { value: 'SPS', label: 'Spouse' },
  { value: 'GUARD', label: 'Guardian' },
];

/** Empty string = relationship omitted (RelatedPerson created without relationship coding). */
const relationshipFieldSchema = z.union([z.enum(relationshipCodeValues), z.literal('')]);

export function episodePartyRelationshipLabel(code: EpisodePartyRelationshipCode): string {
  return EPISODE_PARTY_RELATIONSHIP_OPTIONS.find((o) => o.value === code)?.label ?? 'Parent';
}

/** Maps form value to bot input label, or undefined when left blank. */
export function episodePartyRelationshipForBot(
  value: z.input<typeof relationshipFieldSchema>,
): string | undefined {
  if (!value) return undefined;
  return episodePartyRelationshipLabel(value);
}

export function episodePartyRelationshipSelectData(): Array<{ value: string; label: string }> {
  return EPISODE_PARTY_RELATIONSHIP_OPTIONS.map((o) => ({ value: o.value, label: o.label }));
}

export const addEpisodeMemberFormSchema = z
  .object({
    mode: z.enum(['existing', 'new']),
    participantPatientId: z.string().nullable(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    relationship: relationshipFieldSchema,
  })
  .superRefine((data, ctx) => {
    if (data.mode === 'existing') {
      if (!data.participantPatientId?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Select a client',
          path: ['participantPatientId'],
        });
      }
      return;
    }
    if (!data.firstName.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'First name is required',
        path: ['firstName'],
      });
    }
    if (!data.lastName.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Last name is required',
        path: ['lastName'],
      });
    }
    const email = data.email.trim();
    if (!email) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Email is required',
        path: ['email'],
      });
    } else if (!z.string().email().safeParse(email).success) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter a valid email address',
        path: ['email'],
      });
    }
  });

export type AddEpisodeMemberFormValues = z.input<typeof addEpisodeMemberFormSchema>;

export function getDefaultAddEpisodeMemberFormValues(): AddEpisodeMemberFormValues {
  return {
    mode: 'existing',
    participantPatientId: null,
    firstName: '',
    lastName: '',
    email: '',
    relationship: '',
  };
}
